import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { QRCodeSVG } from 'qrcode.react';
import logoStacked from '../../assets/logo_stacked.png';

const PrintStyles = createGlobalStyle`
  @media print {
    @page {
      size: A4;
      margin: 10mm 15mm;
    }
    
    body {
      margin: 0;
      padding: 0;
    }
    
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
  }
`;

const PrintWrapper = styled.div`
  width: 100%;
  max-width: 210mm; /* A4 width */
  margin: 0 auto;
  
  @media print {
    max-width: 100%;
    margin: 0;
    /* Ensure single page on A4 */
    max-height: 277mm; /* A4 height minus margins */
    overflow: hidden;
  }
`;

const TicketContainer = styled.div`
  background: white;
  width: 100%;
  border: 2px solid #101d40;
  overflow: hidden;
  position: relative;
  box-shadow: 0 4px 20px rgba(16, 29, 64, 0.12);
  
  @media print {
    border: 2px solid #101d40;
    page-break-inside: avoid;
    page-break-before: auto;
    page-break-after: avoid;
    box-shadow: none;
    /* Ensure compact layout for single page */
    font-size: 95%;
  }
`;

const TicketHeader = styled.div`
  background: linear-gradient(135deg, #101d40, #1a2d5a);
  color: white;
  padding: 1.2rem 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border-bottom: 4px solid #9d6d49;
  
  @media print {
    padding: 0.9rem 1.5rem;
  }
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const LogoImage = styled.img`
  height: 50px;
  width: auto;
  
  @media print {
    height: 40px;
  }
`;

const CastleText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CastleName = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Georgia', serif;
  letter-spacing: 2px;
  
  @media print {
    font-size: 1.3rem;
  }
`;

const TicketTitle = styled.div`
  font-size: 0.85rem;
  opacity: 0.9;
  font-weight: 400;
  letter-spacing: 1px;
  margin-top: 0.25rem;
`;

const TicketBody = styled.div`
  padding: 1.2rem;
  position: relative;
  
  @media print {
    padding: 0.7rem;
  }
`;

const TicketMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-bottom: 0.6rem;
  
  @media print {
    gap: 0.6rem;
    margin-bottom: 0.4rem;
  }
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  
  @media print {
    gap: 0.5rem;
  }
`;

const QRCodeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #f8f9fa;
  padding: 0.9rem;
  border-radius: 8px;
  border: 2px dashed #101d40;
  
  @media print {
    padding: 0.5rem;
  }
`;

const ReferenceSection = styled.div`
  background: #f8f7f4;
  border: 2px solid #9d6d49;
  border-radius: 8px;
  padding: 0.9rem 1.2rem;
  text-align: center;
  
  @media print {
    padding: 0.6rem 0.9rem;
  }
`;

const ReferenceLabel = styled.div`
  font-size: 0.7rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 0.3rem;
  
  @media print {
    font-size: 0.65rem;
    margin-bottom: 0.25rem;
  }
`;

const ReferenceNumber = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  letter-spacing: 2.5px;
  font-family: 'Courier New', monospace;
  color: #101d40;
  
  @media print {
    font-size: 1.1rem;
    letter-spacing: 2px;
  }
`;

const DetailsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  
  @media print {
    gap: 0.3rem;
  }
`;

const DetailBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.55rem 0.9rem;
  background: #fff;
  border-radius: 6px;
  border-left: 3px solid #9d6d49;
  
  @media print {
    padding: 0.35rem 0.7rem;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex: 0 0 auto;
  
  @media print {
    font-size: 0.68rem;
  }
`;

const DetailValue = styled.div`
  font-size: 0.95rem;
  color: #101d40;
  font-weight: 700;
  text-align: right;
  flex: 1;
  
  @media print {
    font-size: 0.85rem;
  }
`;

const TicketsSection = styled.div`
  margin-bottom: 0;
  
  @media print {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 0.95rem;
  color: #101d40;
  margin-bottom: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media print {
    font-size: 0.85rem;
    margin-bottom: 0.5rem;
  }
`;

const TicketRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.4rem 0.6rem;
  background: ${props => props.isTotal ? '#f0f0f0' : 'transparent'};
  border-radius: 4px;
  margin-bottom: 0.2rem;
  font-weight: ${props => props.isTotal ? '700' : '500'};
  font-size: 0.85rem;
  border-bottom: ${props => props.isTotal ? 'none' : '1px solid #e0e0e0'};
  
  @media print {
    font-size: 0.75rem;
    padding: 0.3rem 0.5rem;
    margin-bottom: 0.15rem;
  }
`;

const QRSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  text-align: center;
`;

const QRCodeWrapper = styled.div`
  padding: 0.6rem;
  background: white;
  border-radius: 8px;
  border: 2px solid #101d40;
  
  svg {
    display: block;
  }
  
  @media print {
    padding: 0.4rem;
  }
`;

const QRLabel = styled.div`
  font-size: 0.72rem;
  color: #101d40;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 0.5rem;
  
  @media print {
    font-size: 0.68rem;
    margin-bottom: 0.35rem;
  }
`;

const QRInstructions = styled.div`
  font-size: 0.68rem;
  color: #666;
  line-height: 1.25;
  max-width: 180px;
  text-align: center;
  margin-top: 0.4rem;
  
  @media print {
    font-size: 0.62rem;
    margin-top: 0.3rem;
    line-height: 1.2;
  }
`;

const SavingsBanner = styled.div`
  background: linear-gradient(to right, #f0fdf4, #dcfce7);
  color: #166534;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  margin: 0.5rem 0;
  text-align: center;
  border: 2px solid #86efac;
  border-left: 4px solid #22c55e;
  
  @media print {
    padding: 0.5rem 0.7rem;
    margin: 0.4rem 0;
    page-break-inside: avoid;
  }
`;

const SavingsBadge = styled.div`
  display: inline-block;
  background: #22c55e;
  color: white;
  padding: 0.2rem 0.8rem;
  border-radius: 12px;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 0.3rem;
  
  @media print {
    font-size: 0.62rem;
    padding: 0.18rem 0.7rem;
    margin-bottom: 0.25rem;
  }
`;

const SavingsText = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  margin: 0.3rem 0;
  color: #15803d;
  
  @media print {
    font-size: 0.85rem;
    margin: 0.25rem 0;
  }
`;

const SavingsAmount = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: #16a34a;
  
  @media print {
    font-size: 1.1rem;
  }
`;

const FestiveMessage = styled.div`
  background: linear-gradient(135deg, #101d40, #1a2d5a);
  color: white;
  padding: 0.8rem 1rem;
  border-radius: 8px;
  margin: 0.6rem 0;
  text-align: center;
  border-left: 4px solid #9d6d49;
  
  h3 {
    font-size: 0.9rem;
    margin: 0 0 0.3rem 0;
    font-weight: 600;
  }
  
  p {
    font-size: 0.8rem;
    line-height: 1.35;
    margin: 0;
    font-style: italic;
    opacity: 0.95;
  }
  
  @media print {
    padding: 0.55rem 0.8rem;
    margin: 0.45rem 0;
    page-break-inside: avoid;
    
    h3 {
      font-size: 0.8rem;
      margin-bottom: 0.25rem;
    }
    
    p {
      font-size: 0.7rem;
      line-height: 1.25;
    }
  }
`;

const ImportantInfo = styled.div`
  background: #f8f7f4;
  border: 1px solid #9d6d49;
  border-radius: 6px;
  padding: 0.8rem 1rem;
  margin: 0.6rem 0 0.5rem 0;
  
  @media print {
    padding: 0.5rem 0.7rem;
    margin: 0.4rem 0 0.3rem 0;
    page-break-inside: avoid;
  }
`;

const InfoTitle = styled.h4`
  color: #101d40;
  font-size: 0.8rem;
  margin: 0 0 0.4rem 0;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media print {
    font-size: 0.7rem;
    margin-bottom: 0.3rem;
  }
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  
  li {
    padding: 0.25rem 0;
    color: #333;
    font-size: 0.8rem;
    line-height: 1.4;
    padding-left: 1.1rem;
    position: relative;
    
    &::before {
      content: '•';
      color: #9d6d49;
      font-weight: bold;
      position: absolute;
      left: 0;
    }
  }
  
  @media print {
    li {
      font-size: 0.72rem;
      padding: 0.2rem 0 0.2rem 1rem;
      line-height: 1.3;
    }
  }
`;

const TermsSection = styled.div`
  background: #f8f9fa;
  padding: 0.7rem 0.8rem;
  border-radius: 6px;
  margin-top: 0.5rem;
  font-size: 0.7rem;
  color: #666;
  line-height: 1.35;
  border-top: 1px solid #9d6d49;
  
  h5 {
    color: #101d40;
    font-size: 0.72rem;
    margin: 0 0 0.35rem 0;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  p {
    margin: 0.25rem 0;
  }
  
  @media print {
    padding: 0.5rem 0.65rem;
    font-size: 0.62rem;
    margin-top: 0.4rem;
    page-break-inside: avoid;
    line-height: 1.25;
    
    h5 {
      font-size: 0.68rem;
      margin-bottom: 0.25rem;
    }
    
    p {
      margin: 0.2rem 0;
    }
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px solid #e0e0e0;
  color: #666;
  font-size: 0.72rem;
  
  p {
    margin: 0.18rem 0;
  }
  
  .website {
    color: #101d40;
    font-weight: 600;
  }
  
  @media print {
    font-size: 0.65rem;
    margin-top: 0.4rem;
    padding-top: 0.4rem;
    
    p {
      margin: 0.12rem 0;
    }
  }
`;

const LocationNotice = styled.div`
  font-size: 0.7rem;
  color: #666;
  font-style: italic;
  padding: 0.3rem 0.6rem;
  margin-top: 0.2rem;
  margin-left: 0.5rem;
  border-left: 2px solid #9d6d49;
  
  @media print {
    font-size: 0.65rem;
    padding: 0.25rem 0.5rem;
    margin-top: 0.15rem;
  }
`;

const PrintableTicket = ({ 
  bookingReference,
  selectedTickets,
  selectedDate,
  selectedTimeSlot,
  customerDetails,
  totalAmount,
  venposBasket,
  bookingSummary,
  isFamilySaver = false
}) => {
  const lowerTicketNames = (selectedTickets || []).map(t => (t.name || t.title || '').toLowerCase());
  const pageId = venposBasket?.data?.pageId || bookingSummary?.pageId || null;
  const isFlowerGardenShow = (pageId === '1ced1e1a-b442-4dee-b1e1-ae8b5b7bee09') ||
    lowerTicketNames.some(n => n.includes('flower') && n.includes('garden'));
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeSlot) => {
    return timeSlot?.time || timeSlot || 'Not specified';
  };

  // Safety check for required data
  if (!selectedTickets || !selectedDate || !customerDetails) {

    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Unable to load ticket</h2>
        <p>Required booking data is missing. Please try again.</p>
      </div>
    );
  }

  const totalTickets = selectedTickets.reduce((total, ticket) => total + (ticket.quantity || 0), 0);
  
  // Extract discount information
  const basketData = venposBasket?.data;
  const discounts = basketData?.discounts || bookingSummary?.discounts || [];
  const totalSavings = bookingSummary?.totalSavings || discounts.reduce((sum, d) => sum + (d.discountedAmount || 0), 0);
  const hasSavings = totalSavings > 0;
  
  // Detect discount type
  const isFamilySaverDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('FS'));
  const isTravelZooDiscount = discounts.some(d => d.code && d.code.toUpperCase().startsWith('TZ'));
  
  // Calculate prices
  let originalPrice, finalPrice;
  if (bookingSummary?.originalPrice && bookingSummary?.finalPrice !== undefined) {
    originalPrice = bookingSummary.originalPrice;
    finalPrice = bookingSummary.finalPrice;
  } else if (basketData?.totalPrice && basketData?.finalPrice !== undefined) {
    originalPrice = basketData.totalPrice;
    finalPrice = basketData.finalPrice;
  } else {
    originalPrice = totalAmount;
    finalPrice = totalAmount;
  }
  
  const savingsPercentage = originalPrice > 0 ? ((totalSavings / originalPrice) * 100).toFixed(0) : 0;

  return (
    <>
      <PrintStyles />
      <PrintWrapper>
        <TicketContainer className="printable-ticket">
          {/* Header */}
          <TicketHeader>
          <LogoSection>
            <LogoImage 
              src={logoStacked} 
              alt="Belvoir Castle"
            />
            <CastleText>
              <CastleName>BELVOIR CASTLE</CastleName>
              <TicketTitle>Admission Ticket</TicketTitle>
            </CastleText>
          </LogoSection>
        </TicketHeader>

        <TicketBody>
          <TicketMain>
            {/* Booking Reference */}
            <DetailsSection>
              <ReferenceSection>
                <ReferenceLabel>Booking Reference</ReferenceLabel>
                <ReferenceNumber>{bookingReference}</ReferenceNumber>
              </ReferenceSection>

              <DetailsGrid>
                <DetailBox>
                  <DetailLabel>Visit Date</DetailLabel>
                  <DetailValue>{formatDate(selectedDate)}</DetailValue>
                </DetailBox>

                <DetailBox>
                  <DetailLabel>Arrival Time</DetailLabel>
                  <DetailValue>{formatTime(selectedTimeSlot)}</DetailValue>
                </DetailBox>

                <DetailBox>
                  <DetailLabel>Guest Name</DetailLabel>
                  <DetailValue>{customerDetails.firstName} {customerDetails.lastName}</DetailValue>
                </DetailBox>

                <DetailBox>
                  <DetailLabel>Number of Guests</DetailLabel>
                  <DetailValue>{totalTickets} {totalTickets === 1 ? 'Guest' : 'Guests'}</DetailValue>
                </DetailBox>

                <DetailBox>
                  <DetailLabel>Total Paid (inc. VAT)</DetailLabel>
                  <DetailValue>£{finalPrice.toFixed(2)}</DetailValue>
                </DetailBox>
              </DetailsGrid>
            </DetailsSection>

            {/* Savings Banner - Show if discount applied */}
            {hasSavings && (
              <SavingsBanner>
                <SavingsBadge>
                  {isFamilySaverDiscount 
                    ? 'FAMILY SAVER' 
                    : isTravelZooDiscount 
                      ? 'TRAVEL ZOO' 
                      : 'DISCOUNT'}
                </SavingsBadge>
                <SavingsText>
                  You Saved <SavingsAmount>£{totalSavings.toFixed(2)}</SavingsAmount>
                </SavingsText>
              </SavingsBanner>
            )}

            {/* QR Code Section */}
            <QRCodeSection>
              <QRSection>
                <QRLabel>PRESENT AT VISITOR HUB</QRLabel>
                <QRCodeWrapper>
                  <QRCodeSVG 
                    value={bookingReference}
                    size={100}
                    level="H"
                    includeMargin={false}
                  />
                </QRCodeWrapper>
                <QRInstructions>
                  Scan code or show reference
                </QRInstructions>
              </QRSection>
            </QRCodeSection>

            {/* Ticket Breakdown - Always show */}
            <DetailsSection>
              <TicketsSection>
                <SectionTitle>Tickets</SectionTitle>
                {selectedTickets && selectedTickets.length > 0 ? (
                  selectedTickets
                    .filter(ticket => ticket.quantity > 0)
                    .map((ticket, index) => {
                      // Use totalPrice if available (from grouped tickets), otherwise calculate
                      const ticketPrice = ticket.totalPrice || (ticket.price * ticket.quantity) || 0;
                      const ticketName = ticket.title || ticket.name || 'Ticket';
                      const isRetailVillageGrotto = ticketName.toLowerCase().includes("santa's grotto") && 
                                                     ticketName.toLowerCase().includes("retail village");
                      
                      return (
                        <React.Fragment key={ticket.webSaleItemId || ticket.id || index}>
                          <TicketRow>
                            <span>{ticket.quantity}x {ticketName}</span>
                            <span>£{ticketPrice.toFixed(2)}</span>
                          </TicketRow>
                          {isRetailVillageGrotto && (
                            <LocationNotice>
                              This is a 5 minute walk from the castle
                            </LocationNotice>
                          )}
                        </React.Fragment>
                      );
                    })
                ) : (
                  <TicketRow>
                    <span>No ticket details available</span>
                  </TicketRow>
                )}
              </TicketsSection>
            </DetailsSection>
          </TicketMain>

          {!isFlowerGardenShow && (
            <FestiveMessage>
              <h3>Thank you from Belvoir Castle</h3>
              <p style={{ fontStyle: 'italic', fontSize: '0.95rem', margin: '0.5rem 0', color: '#2e7d32', fontWeight: '600' }}>
                Your Castle, Your Crowd, Your Saving
              </p>
              <p>We look forward to welcoming!</p>
            </FestiveMessage>
          )}

          <ImportantInfo>
            <InfoTitle>Information</InfoTitle>
            <InfoList>
              <li>Arrive at scheduled time • Parking £3 • Confirmation: {customerDetails.email}</li>
            </InfoList>
          </ImportantInfo>

          <TermsSection>
            <h5>Terms</h5>
            <p>
              <strong>Non-refundable</strong> • Valid for date/time shown • Present at Visitor Hub • 
              Questions: <strong>info@belvoircastle.com</strong> | <strong>01476 871001</strong>
            </p>
          </TermsSection>

          <Footer>
            <p><span className="website">www.belvoircastle.com</span></p>
            <p>Belvoir Castle, Grantham, Leicestershire, NG32 1PE</p>
          </Footer>
        </TicketBody>
      </TicketContainer>
    </PrintWrapper>
    </>
  );
};

export default PrintableTicket;

